export interface I18nString {
  [key: string]: string | I18nString;
}

export const translations: Record<string, I18nString> = {
  en: {
    dashboard: "Dashboard",
    createTour: "Create New Tour",
    save: "Save Changes",
    cancel: "Cancel",
    welcome: "Welcome back",
    stats: {
      revenue: "Total Revenue",
      bookings: "Active Bookings"
    },
    loading: "Loading...",
    nav: {
      home: "Home",
      expeditions: "Expeditions",
      destinations: "Destinations",
      contact: "Contact Us",
      accommodations: "Private Villas",
      activities: "Activities",
      blog: "Island Journal"
    },
    cta: {
      planEscape: "Plan My Escape",
      viewDetails: "View Details",
      explore: "Explore",
      learnMore: "Learn More",
      bookNow: "Book Now",
      inquireNow: "Inquire Now",
      viewAllTours: "View All Tours",
      viewAllActivities: "View All Activities",
      viewAllAccommodations: "View All Accommodations",
      visitBlog: "Visit Our Blog",
      readMore: "Read More",
      checkAvailability: "Check Availability",
      reserveSpot: "Reserve Your Spot",
      viewDestinations: "View Destinations",
      countryInfo: "Country Info",
      planCustomTour: "Plan Custom Tour"
    },
    sections: {
      about: "About",
      amenities: "Amenities",
      rooms: "Accommodations",
      itinerary: "Itinerary",
      gallery: "Gallery",
      requirements: "Requirements",
      aboutActivity: "About this Activity",
      overview: "Overview",
      location: "Location"
    },
    activity: {
      pricePerPerson: "Price Per Person",
      details: "Activity Details"
    },
    footer: {
      newsletter: "Newsletter",
      subscribe: "Subscribe",
      placeholder: "YOUR EMAIL",
      newsletterText: "Subscribe for monthly island guides and exclusive last-minute offers.",
      copyright: "© {year} {siteName}",
      expeditions: "Expeditions",
      theIsland: "The Island",
      boutiqueText: "Curated boutique experiences across the archipelago. From private islands to historic Stone Town retreats.",
      luxurySafaris: "Luxury Safaris",
      marineExcursions: "Marine Excursions",
      islandJournal: "Island Journal",
      privateVillas: "Private Villas",
      ourTeam: "Our Team",
      admin: "Admin",
      privacy: "Privacy",
      terms: "Terms"
    },
    blog: {
      journal: "Island Journal",
      subtitle: "Stories, guides, and inspiration from the African bush.",
      searchPlaceholder: "Search articles...",
      noArticles: "No articles found",
      tryAdjusting: "Try adjusting your filters or search terms.",
      clearFilters: "Clear all filters",
      contributor: "Contributor",
      backToJournal: "Back to Journal",
      author: "Author",
      share: "Share",
      aboutAuthor: "About the Author"
    },
    contact: {
      title: "Let's Plan Your Safari Adventure",
      subtitle: "Our safari experts are here to help you create unforgettable memories. From custom itineraries to instant quotes, we're just a message away.",
      respondTime: "Typically respond within 2 hours",
      stats: {
        emergency: "Emergency Support",
        experience: "Years Experience",
        travelers: "Happy Travelers"
      },
      methods: {
        call: "Call Us",
        email: "Email Us",
        visit: "Visit Office",
        chat: "Live Chat"
      },
      form: {
        heading: "Send Us a Message",
        help: "What can we help you with?",
        getQuote: "Get Quote",
        customSafari: "Custom Safari",
        generalInfo: "General Info",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        message: "Message",
        placeholder: "Tell us about your dream safari...",
        sending: "Sending...",
        sendMessage: "Send Message",
        success: "Message Sent Successfully!",
        respondLater: "We'll get back to you within 24 hours."
      }
    },
    destinations: {
      title: "Explore Destinations",
      subtitle: "Journey through the most breathtaking landscapes in Tanzania and Zanzibar.",
      search: "Find a destination...",
      tours: "Tours",
      noResults: "No destinations found",
      adjustSearch: "Try adjusting your search terms.",
      clearSearch: "Clear search",
      overview: "Overview",
      whyVisit: "Why Visit {name}?",
      bestTime: "Best Time to Visit",
      keyFacts: "Key Facts",
      area: "Area",
      coordinates: "Coordinates",
      planTrip: "Plan Your Trip"
    },
    accommodations: {
      title: "Luxury Lodges & Camps",
      subtitle: "Handpicked sanctuaries where luxury meets the wilderness.",
      search: "Find your stay...",
      noResults: "No accommodations found",
      adjustFilters: "Try adjusting your filters or search terms.",
      clearFilters: "Clear all filters",
      priceRange: "Price Range",
      all: "All",
      amenities: "Amenities",
      details: "Details",
      location: "Location",
      rooms: "Available Rooms",
      contactForRates: "Contact for rates",
      checkIn: "Check-in",
      checkOut: "Check-out",
      diningOptions: "Dining Options",
      noRoomDetails: "Detailed room information not available. Please contact us for specific room types.",
      makeReservation: "Make a Reservation",
      directLine: "Direct Line",
      emailReservations: "Email Reservations",
      capacity: "Capacity",
      guests: "Guests",
      bookNow: "Book Now"
    },
    home: {
      hero: {
        badge: "Africa's #1 Budget Safari Operator",
        title: "Discover Africa's Wild Beauty",
        subtitle: "On a Budget That Works",
        description: "Experience unforgettable safari adventures across {count} African countries without breaking the bank. Professional guides, comfortable accommodations, and incredible wildlife encounters await.",
        ctaExplore: "Explore Safari Tours",
        ctaCustom: "Plan Custom Safari"
      },
      trust: {
        licensed: "Licensed & Insured",
        certified: "TATO Certified",
        experience: "{years}+ Years",
        experienceSub: "Expert Experience",
        travelers: "{count}+ Travelers",
        travelersSub: "5-Star Reviews"
      },
      search: {
        title: "Find Your Perfect Safari",
        destination: "Destination",
        allDestinations: "All Destinations",
        travelDate: "Travel Date",
        budget: "Budget (USD)",
        anyBudget: "Any Budget",
        button: "Search Tours"
      },
      stats: {
        tours: "Safari Tours",
        countries: "Countries",
        support: "Support"
      },
      featured: {
        title: "Featured Safari Tours",
        subtitle: "Handpicked adventures that offer the best value for money across Africa's most spectacular destinations",
        popular: "Most Popular",
        viewAll: "View All Tours"
      },
      whyChoose: {
        title: "Why Choose {name}?",
        description: "For over {years} years, we've been making African safari dreams accessible to travelers from around the world. Our mission is simple: provide authentic, safe, and affordable safari experiences without compromising on quality.",
        expertise: "Local Expertise",
        expertiseSub: "Native guides with deep knowledge of wildlife and local cultures",
        groups: "Small Groups",
        groupsSub: "Maximum 8-10 people per tour for personalized experiences",
        pricing: "Transparent Pricing",
        pricingSub: "No hidden fees - what you see is what you pay",
        cta: "Learn More About Us",
        watchStory: "Watch Our Story"
      },
      accommodations: {
        title: "Comfortable Safari Accommodations",
        subtitle: "Rest easy in carefully selected lodges and camps that balance comfort, authenticity, and affordability",
        night: "per night"
      },
      activities: {
        title: "Unforgettable Safari Activities",
        subtitle: "Experience Africa's wilderness through diverse activities designed for adventure seekers and nature lovers",
        included: "Included"
      },
      blog: {
        title: "Latest from Our Travel Blog",
        subtitle: "Discover insider tips, wildlife insights, and inspiring stories from our African safari adventures",
        by: "By {author}"
      },
      ready: {
        title: "Ready for Your African Adventure?",
        subtitle: "Subscribe to get exclusive deals, safari tips, and be the first to know about new destinations",
        placeholder: "Enter your email address",
        subscribe: "Subscribe",
        noSpam: "No spam, only adventure! Unsubscribe anytime."
      }
    },
    tours: {
      title: "Our Expeditions",
      subtitle: "Curated journeys through the untamed landscapes of Zanzibar and Tanzania.",
      search: "Search expeditions...",
      duration: "Duration",
      groupSize: "Group Size",
      startingFrom: "Starting From",
      max: "Max",
      noResults: "No expeditions found",
      adjustFilters: "Try adjusting your filters or search terms.",
      clearFilters: "Clear all filters",
      bestseller: "Bestseller"
    },
    activities: {
      title: "Experiences & Activities",
      subtitle: "Discover the hidden gems and thrilling adventures that await you.",
      search: "Search activities...",
      from: "From",
      noResults: "No activities found",
      adjustFilters: "Try adjusting your filters or search terms.",
      clearFilters: "Clear all filters"
    },
    cookie: {
      title: "We Value Your Privacy",
      subtitle: "Cookie & Privacy Preferences",
      description: "We use cookies and similar technologies to enhance your safari planning experience, analyze our site traffic, and personalize content. Your choice helps us preserve the integrity of our digital sanctuary.",
      tabs: {
        consent: "Consent",
        details: "Details",
        about: "About"
      },
      categories: {
        Necessary: "Necessary",
        Preferences: "Preferences",
        Statistics: "Statistics",
        Marketing: "Marketing"
      },
      about: {
        title: "About Cookies",
        how: "How do we use cookies?",
        contact: "Contact Us",
        p1: "Cookies are small text files that are used to store small pieces of information. They are stored on your device when the website is loaded on your browser. These cookies help us make the website function properly, make it more secure, provide better user experience, and understand how the website performs.",
        p2: "As most of the online services, our website uses first-party and third-party cookies for several purposes. First-party cookies are mostly necessary for the website to function the right way, and they do not collect any of your personally identifiable data.",
        p3: "If you have any questions about our cookie policy, please contact our spiritual guide at the support desk."
      },
      buttons: {
        rejectAll: "Reject All",
        allowSelection: "Allow Selection",
        allowAll: "Allow All"
      },
      required: "Required",
      policiesActive: "{count} Policies active",
      noPolicies: "No specific policies listed for this category."
    }
  },
  es: {
    dashboard: "Panel de Control",
    createTour: "Crear Nuevo Tour",
    save: "Guardar Cambios",
    cancel: "Cancelar",
    welcome: "Bienvenido de nuevo",
    stats: {
      revenue: "Ingresos Totales",
      bookings: "Reservas Activas"
    },
    loading: "Cargando...",
    nav: {
      home: "Inicio",
      expeditions: "Expediciones",
      destinations: "Destinos",
      contact: "Contáctenos",
      accommodations: "Villas Privadas",
      activities: "Actividades",
      blog: "Diario de la Isla"
    },
    cta: {
      planEscape: "Planear Mi Escape",
      viewDetails: "Ver Detalles",
      explore: "Explorar",
      learnMore: "Saber Más",
      bookNow: "Reservar Ahora",
      inquireNow: "Consultar Ahora",
      viewAllTours: "Ver Todos los Tours",
      viewAllActivities: "Ver Todas las Actividades",
      viewAllAccommodations: "Ver Todos los Alojamientos",
      visitBlog: "Visite Nuestro Blog",
      readMore: "Leer Más",
      checkAvailability: "Ver Disponibilidad",
      reserveSpot: "Reserve su Lugar"
    },
    sections: {
      about: "Sobre",
      amenities: "Comodidades",
      rooms: "Alojamientos",
      itinerary: "Itinerario",
      gallery: "Galería",
      requirements: "Requisitos",
      aboutActivity: "Sobre esta Actividad",
      overview: "Visión General"
    },
    activity: {
      pricePerPerson: "Precio Por Persona",
      details: "Detalles de la Actividad"
    },
    footer: {
      newsletter: "Boletín Informativo",
      subscribe: "Suscribirse",
      placeholder: "SU CORREO",
      newsletterText: "Suscríbete para recibir guías mensuales de la isla y ofertas exclusivas de última hora.",
      copyright: "© {year} {siteName}"
    },
    blog: {
      journal: "Diario de la Isla",
      subtitle: "Historias, guías e inspiración de la sabana africana.",
      searchPlaceholder: "Buscar artículos...",
      noArticles: "No se encontraron artículos",
      tryAdjusting: "Intente ajustar sus filtros o términos de búsqueda.",
      clearFilters: "Borrar todos los filtros",
      contributor: "Colaborador",
      backToJournal: "Volver al Diario",
      author: "Autor",
      share: "Compartir",
      aboutAuthor: "Sobre el Autor"
    },
    contact: {
      title: "Planeemos su Aventura de Safari",
      subtitle: "Nuestros expertos en safari están aquí para ayudarlo a crear recuerdos inolvidables. Desde itinerarios personalizados hasta presupuestos instantáneos, estamos a solo un mensaje de distancia.",
      respondTime: "Normalmente respondemos en 2 horas",
      stats: {
        emergency: "Soporte de Emergencia",
        experience: "Años de Experiencia",
        travelers: "Viajeros Felices"
      },
      methods: {
        call: "Llámenos",
        email: "Envíenos un Email",
        visit: "Visite la Oficina",
        chat: "Chat en Vivo"
      },
      form: {
        heading: "Envíenos un Mensaje",
        help: "¿En qué podemos ayudarle?",
        getQuote: "Obtener Presupuesto",
        customSafari: "Safari Personalizado",
        generalInfo: "Información General",
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo Electrónico",
        phone: "Número de Teléfono",
        message: "Mensaje",
        placeholder: "Cuéntenos sobre su safari soñado...",
        sending: "Enviando...",
        sendMessage: "Enviar Mensaje",
        success: "¡Mensaje Enviado con Éxito!",
        respondLater: "Nos pondremos en contacto con usted en 24 horas."
      }
    },
    destinations: {
      title: "Explorar Destinos",
      subtitle: "Viaje a través de los paisajes más impresionantes de Tanzania y Zanzíbar.",
      search: "Encontrar un destino...",
      tours: "Tours",
      noResults: "No se encontraron destinos",
      adjustSearch: "Intente ajustar sus términos de búsqueda.",
      clearSearch: "Borrar búsqueda",
      overview: "Visión General",
      whyVisit: "¿Por qué visitar {name}?",
      bestTime: "Mejor Época para Visitar",
      keyFacts: "Datos Clave",
      area: "Área",
      coordinates: "Coordenadas",
      planTrip: "Planifique su Viaje"
    },
    accommodations: {
      title: "Logias y Campamentos de Lujo",
      subtitle: "Santuarios seleccionados donde el lujo se encuentra con la naturaleza.",
      search: "Encuentre su estancia...",
      noResults: "No se encontraron alojamientos",
      adjustFilters: "Intente ajustar sus filtros o términos de búsqueda.",
      clearFilters: "Borrar todos los filtros",
      priceRange: "Rango de Precios",
      all: "Todos",
      amenities: "Servicios",
      details: "Detalles",
      location: "Ubicación",
      rooms: "Habitaciones Disponibles",
      contactForRates: "Contactar para tarifas",
      checkIn: "Entrada",
      checkOut: "Salida",
      diningOptions: "Opciones de Comedor",
      noRoomDetails: "Información detallada de la habitación no disponible. Por favor, contáctenos para tipos específicos.",
      makeReservation: "Hacer una Reserva",
      directLine: "Línea Directa",
      emailReservations: "Reservas por Email",
      capacity: "Capacidad",
      guests: "Huéspedes"
    },
    home: {
      hero: {
        badge: "Operador de Safari Económico #1 en África",
        title: "Descubre la Belleza Salvaje de África",
        subtitle: "Con un Presupuesto que Funciona",
        description: "Vive aventuras de safari inolvidables en {count} países africanos sin arruinarte. Guías profesionales, alojamientos cómodos e increíbles encuentros con la fauna te esperan.",
        ctaExplore: "Explorar Tours de Safari",
        ctaCustom: "Planificar Safari Personalizado"
      },
      trust: {
        licensed: "Licenciado y Asegurado",
        certified: "Certificado por TATO",
        experience: "{years}+ Años",
        experienceSub: "Experiencia de Expertos",
        travelers: "{count}+ Viajeros",
        travelersSub: "Reseñas de 5 Estrellas"
      },
      search: {
        title: "Encuentra tu Safari Perfecto",
        destination: "Destino",
        allDestinations: "Todos los Destinos",
        travelDate: "Fecha de Viaje",
        budget: "Presupuesto (USD)",
        anyBudget: "Cualquier Presupuesto",
        button: "Buscar Tours"
      },
      stats: {
        tours: "Tours de Safari",
        countries: "Países",
        support: "Soporte"
      },
      featured: {
        title: "Tours de Safari Destacados",
        subtitle: "Aventuras seleccionadas que ofrecen la mejor relación calidad-precio en los destinos más espectaculares de África",
        popular: "Más Popular",
        viewAll: "Ver Todos los Tours"
      },
      whyChoose: {
        title: "¿Por Qué Elegir {name}?",
        description: "Durante más de {years} años, hemos hecho que los sueños de safari en África sean accesibles para viajeros de todo el mundo. Nuestra misión es simple: proporcionar experiencias de safari auténticas, seguras y asequibles sin comprometer la calidad.",
        expertise: "Experiencia Local",
        expertiseSub: "Guías nativos con profundo conocimiento de la fauna y las culturas locales",
        groups: "Grupos Pequeños",
        groupsSub: "Máximo 8-10 personas por tour para experiencias personalizadas",
        pricing: "Precios Transparentes",
        pricingSub: "Sin cargos ocultos: lo que ves es lo que pagas",
        cta: "Saber Más Sobre Nosotros",
        watchStory: "Ver Nuestra Historia"
      },
      accommodations: {
        title: "Alojamientos de Safari Cómodos",
        subtitle: "Descansa tranquilamente en lodges y campamentos cuidadosamente seleccionados que equilibran comodidad, autenticidad y asequibilidad",
        night: "por noche"
      },
      activities: {
        title: "Actividades de Safari Inolvidables",
        subtitle: "Experimenta la naturaleza salvaje de África a través de diversas actividades diseñadas para buscadores de aventuras y amantes de la naturaleza",
        included: "Incluido"
      },
      blog: {
        title: "Lo Último de Nuestro Blog de Viajes",
        subtitle: "Descubre consejos de expertos, conocimientos sobre la fauna y hazañas inspiradoras de nuestras aventuras de safari en África",
        by: "Por {author}"
      },
      ready: {
        title: "¿Listo para tu Aventura Africana?",
        subtitle: "Suscríbete para recibir ofertas exclusivas, consejos de safari y ser el primero en conocer nuevos destinos",
        placeholder: "Introduce tu correo electrónico",
        subscribe: "Suscribirse",
        noSpam: "¡Nada de spam, solo aventura! Date de baja en cualquier momento."
      }
    },
    tours: {
      title: "Nuestras Expediciones",
      subtitle: "Viajes seleccionados a través de los paisajes indómitos de Zanzíbar y Tanzania.",
      search: "Buscar expediciones...",
      duration: "Duración",
      groupSize: "Tamaño del Grupo",
      startingFrom: "Desde",
      max: "Máx",
      noResults: "No se encontraron expediciones",
      adjustFilters: "Intente ajustar sus filtros o términos de búsqueda.",
      clearFilters: "Borrar todos los filtros",
      bestseller: "Más Vendido"
    },
    activities: {
      title: "Experiencias y Actividades",
      subtitle: "Descubra las joyas ocultas y las aventuras emocionantes que le esperan.",
      search: "Buscar actividades...",
      from: "Desde",
      noResults: "No se encontraron actividades",
      adjustFilters: "Intente ajustar sus filtros o términos de búsqueda.",
      clearFilters: "Borrar todos los filtros"
    },
    cookie: {
      title: "Valoramos su Privacidad",
      subtitle: "Preferencias de Cookies y Privacidad",
      description: "Utilizamos cookies y tecnologías similares para mejorar su experiencia de planificación de safari, analizar el tráfico de nuestro sitio y personalizar el contenido. Su elección nos ayuda a preservar la integridad de nuestro santuario digital.",
      tabs: {
        consent: "Consentimiento",
        details: "Detalles",
        about: "Acerca de"
      },
      categories: {
        Necessary: "Necesarias",
        Preferences: "Preferencias",
        Statistics: "Estadísticas",
        Marketing: "Marketing"
      },
      about: {
        title: "Acerca de las Cookies",
        how: "¿Cómo utilizamos las cookies?",
        contact: "Contáctenos",
        p1: "Las cookies son pequeños archivos de texto que se utilizan para almacenar pequeñas piezas de información. Se almacenan en su dispositivo cuando el sitio web se carga en su navegador. Estas cookies nos ayudan a que el sitio web funcione correctamente, sea más seguro, brinde una mejor experiencia de usuario y comprenda cómo funciona el sitio web.",
        p2: "Como la mayoría de los servicios en línea, nuestro sitio web utiliza cookies propias y de terceros para diversos fines. Las cookies propias son principalmente necesarias para que el sitio web funcione correctamente y no recopilan ninguno de sus datos de identificación personal.",
        p3: "Si tiene alguna pregunta sobre nuestra política de cookies, póngase en contacto con nuestro guía espiritual en el mostrador de soporte."
      },
      buttons: {
        rejectAll: "Rechazar Todo",
        allowSelection: "Permitir Selección",
        allowAll: "Permitir Todo"
      },
      required: "Requerido",
      policiesActive: "{count} Políticas activas",
      noPolicies: "No hay políticas específicas enumeradas para esta categoría."
    }
  },
  fr: {
    dashboard: "Tableau de bord",
    createTour: "Créer un nouveau circuit",
    save: "Sauvegarder",
    cancel: "Annuler",
    welcome: "Bon retour",
    stats: {
      revenue: "Revenu Total",
      bookings: "Réservations Actives"
    },
    loading: "Chargement...",
    nav: {
      home: "Accueil",
      expeditions: "Expéditions",
      destinations: "Destinations",
      contact: "Contactez-nous",
      accommodations: "Villas Privées",
      activities: "Activités",
      blog: "Journal de l'Île"
    },
    cta: {
      planEscape: "Planifier Mon Évasion",
      viewDetails: "Voir Détails",
      explore: "Explorer",
      learnMore: "En Savoir Plus",
      bookNow: "Réserver Maintenant",
      inquireNow: "Demande de Renseignements",
      viewAllTours: "Voir Tous les Circuits",
      viewAllActivities: "Voir Toutes les Activités",
      viewAllAccommodations: "Voir Tous les Hébergements",
      visitBlog: "Visitez Notre Blog",
      readMore: "Lire la Suite",
      checkAvailability: "Vérifier la Disponibilité",
      reserveSpot: "Réservez Votre Place",
      viewDestinations: "Voir les Destinations",
      countryInfo: "Infos Pays",
      planCustomTour: "Planifier un Tour Personnalisé"
    },
    sections: {
      about: "À Propos",
      amenities: "Équipements",
      rooms: "Hébergements",
      itinerary: "Itinéraire",
      gallery: "Galerie",
      requirements: "Conditions Requises",
      aboutActivity: "À Propos de cette Activité",
      overview: "Aperçu"
    },
    activity: {
      pricePerPerson: "Prix Par Personne",
      details: "Détails de l'Activité"
    },
    footer: {
      newsletter: "Bulletin d'information",
      subscribe: "S'abonner",
      placeholder: "VOTRE EMAIL",
      newsletterText: "Inscrivez-vous pour recevoir des guides mensuels sur l'île et des offres de dernière minute exclusives.",
      copyright: "© {year} {siteName}",
      expeditions: "Expéditions",
      theIsland: "L'Île",
      boutiqueText: "Expériences boutique organisées à travers l'archipel. Des îles privées aux retraites historiques de Stone Town.",
      luxurySafaris: "Safaris de Luxe",
      marineExcursions: "Excursions Marines",
      islandJournal: "Journal de l'Île",
      privateVillas: "Villas Privées",
      ourTeam: "Notre Équipe",
      admin: "Admin",
      privacy: "Confidentialité",
      terms: "Conditions"
    },
    blog: {
      journal: "Journal de l'Île",
      subtitle: "Histoires, guides et inspiration de la brousse africaine.",
      searchPlaceholder: "Rechercher des articles...",
      noArticles: "Aucun article trouvé",
      tryAdjusting: "Essayez d'ajuster vos filtres ou vos termes de recherche.",
      clearFilters: "Effacer tous les filtres",
      contributor: "Contributeur",
      backToJournal: "Retour au Journal",
      author: "Auteur",
      share: "Partager",
      aboutAuthor: "À Propos de l'Auteur"
    },
    contact: {
      title: "Planifions votre aventure de safari",
      subtitle: "Nos experts en safari sont là pour vous aider à créer des souvenirs inoubliables. Des itinéraires personnalisés aux devis instantanés, nous sommes à un message de distance.",
      respondTime: "Réponse typique en 2 heures",
      stats: {
        emergency: "Support d'Urgence",
        experience: "Années d'Expérience",
        travelers: "Voyageurs Heureux"
      },
      methods: {
        call: "Appelez-nous",
        email: "Envoyez-nous un Email",
        visit: "Visitez le Bureau",
        chat: "Chat en Direct"
      },
      form: {
        heading: "Envoyez-nous un Message",
        help: "Comment pouvons-nous vous aider ?",
        getQuote: "Obtenir un Devis",
        customSafari: "Safari Personnalisé",
        generalInfo: "Infos Générales",
        firstName: "Prénom",
        lastName: "Nom",
        email: "Adresse Email",
        phone: "Numéro de Téléphone",
        message: "Message",
        placeholder: "Parlez-nous de votre safari de rêve...",
        sending: "Envoi en cours...",
        sendMessage: "Envoyer le Message",
        success: "Message envoyé avec succès !",
        respondLater: "Nous vous répondrons dans les 24 heures."
      }
    },
    destinations: {
      title: "Explorer les Destinations",
      subtitle: "Voyagez à travers les paysages les plus époustouflants de Tanzanie et de Zanzibar.",
      search: "Trouver une destination...",
      tours: "Circuits",
      noResults: "Aucune destination trouvée",
      adjustSearch: "Essayez d'ajuster vos termes de recherche.",
      clearSearch: "Effacer la recherche",
      overview: "Aperçu",
      whyVisit: "Pourquoi visiter {name} ?",
      bestTime: "Meilleur Moment pour Visiter",
      keyFacts: "Informations Clés",
      area: "Superficie",
      coordinates: "Coordonnées",
      planTrip: "Planifiez Votre Voyage"
    },
    accommodations: {
      title: "Lodges & Camps de Luxe",
      subtitle: "Des sanctuaires triés sur le volet où le luxe rencontre la nature sauvage.",
      search: "Trouvez votre séjour...",
      noResults: "Aucun hébergement trouvé",
      adjustFilters: "Essayez d'ajuster vos filtres ou vos termes de recherche.",
      clearFilters: "Effacer tous les filtres",
      priceRange: "Gamme de prix",
      all: "Tout",
      amenities: "Équipements",
      details: "Détails",
      location: "Emplacement",
      rooms: "Chambres Disponibles",
      contactForRates: "Contactez-nous pour les tarifs",
      checkIn: "Arrivée",
      checkOut: "Départ",
      diningOptions: "Options de Restauration",
      noRoomDetails: "Informations détaillées sur les chambres non disponibles. Veuillez nous contacter pour les types spécifiques.",
      makeReservation: "Faire une Réservation",
      directLine: "Ligne Directe",
      emailReservations: "Réservations par Email",
      capacity: "Capacité",
      guests: "Invités"
    },
    home: {
      hero: {
        badge: "Opérateur de Safari à Petit Budget #1 en Afrique",
        title: "Découvrez la Beauté Sauvage de l'Afrique",
        subtitle: "Avec un Budget qui Fonctionne",
        description: "Vivez des aventures de safari inoubliables dans {count} pays africains sans vous ruiner. Des guides professionnels, des hébergements confortables et des rencontres incroyables avec la faune vous attendent.",
        ctaExplore: "Explorer les Tours de Safari",
        ctaCustom: "Planifier un Safari Personnalisé"
      },
      trust: {
        licensed: "Licencié et Assuré",
        certified: "Certifié par TATO",
        experience: "{years}+ Années",
        experienceSub: "Expérience d'Expert",
        travelers: "{count}+ Voyageurs",
        travelersSub: "Avis 5 Étoiles"
      },
      search: {
        title: "Trouvez votre Safari Parfait",
        destination: "Destination",
        allDestinations: "Toutes les Destinations",
        travelDate: "Date de Voyage",
        budget: "Budget (USD)",
        anyBudget: "Tout Budget",
        button: "Rechercher des Tours"
      },
      stats: {
        tours: "Tours de Safari",
        countries: "Pays",
        support: "Support"
      },
      featured: {
        title: "Tours de Safari en Vedette",
        subtitle: "Des aventures triées sur le volet qui offrent le meilleur rapport qualité-prix dans les destinations les plus spectaculaires d'Afrique",
        popular: "Plus Populaire",
        viewAll: "Voir Tous les Tours"
      },
      whyChoose: {
        title: "Pourquoi Choisir {name} ?",
        description: "Depuis plus de {years} ans, nous rendons les rêves de safari en Afrique accessibles aux voyageurs du monde entier. Notre mission est simple : offrir des expériences de safari authentiques, sûres et abordables sans compromis sur la qualité.",
        expertise: "Expertise Locale",
        expertiseSub: "Des guides natifs ayant une connaissance approfondie de la faune et des cultures locales",
        groups: "Petits Groupes",
        groupsSub: "Maximum 8-10 personnes par tour pour des expériences personnalisées",
        pricing: "Prix Transparents",
        pricingSub: "Pas de frais cachés - ce que vous voyez est ce que vous payez",
        cta: "En Savoir Plus sur Nous",
        watchStory: "Regarder Notre Histoire"
      },
      accommodations: {
        title: "Hébergements de Safari Confortables",
        subtitle: "Reposez-vous dans des lodges et des camps soigneusement sélectionnés qui équilibrent confort, authenticité et abordabilité",
        night: "par nuit"
      },
      activities: {
        title: "Activités de Safari Inoubliables",
        subtitle: "Découvrez la nature sauvage de l'Afrique à travers diverses activités conçues pour les amateurs d'aventure et de nature",
        included: "Inclus"
      },
      blog: {
        title: "Dernières Nouvelles de Notre Blog de Voyage",
        subtitle: "Découvrez des conseils d'initiés, des aperçus de la faune et des histoires inspirantes de nos aventures de safari en Afrique",
        by: "Par {author}"
      },
      ready: {
        title: "Prêt pour Votre Aventure Africaine ?",
        subtitle: "Abonnez-vous pour recevoir des offres exclusives, des conseils de safari et être le premier à connaître les nouvelles destinations",
        placeholder: "Entrez votre adresse email",
        subscribe: "S'abonner",
        noSpam: "Pas de spam, seulement de l'aventure ! Désinscrivez-vous à tout moment."
      }
    },
    tours: {
      title: "Nos Expéditions",
      subtitle: "Des voyages organisés à travers les paysages indomptés de Zanzibar et de Tanzanie.",
      search: "Rechercher des expéditions...",
      duration: "Durée",
      groupSize: "Taille du Groupe",
      startingFrom: "À partir de",
      max: "Max",
      noResults: "Aucune expédition trouvée",
      adjustFilters: "Essayez d'ajuster vos filtres ou vos termes de recherche.",
      clearFilters: "Effacer tous les filtres",
      bestseller: "Meilleure vente"
    },
    activities: {
      title: "Expériences & Activités",
      subtitle: "Découvrez les joyaux cachés et les aventures palpitantes qui vous attendent.",
      search: "Rechercher des activités...",
      from: "À partir de",
      noResults: "Aucune activité trouvée",
      adjustFilters: "Essayez d'ajuster vos filtres ou vos termes de recherche.",
      clearFilters: "Effacer tous les filtres"
    }
  },
  ar: {
    dashboard: "لوحة القيادة",
    createTour: "إنشاء جولة جديدة",
    save: "حفظ التغييرات",
    cancel: "إلغاء",
    welcome: "مرحبًا بعودتك",
    stats: {
      revenue: "إجمالي الإيرادات",
      bookings: "الحجوزات النشطة"
    },
    loading: "جاري التحميل...",
    nav: {
      home: "الرئيسية",
      expeditions: "الرحلات الاستكشافية",
      destinations: "الوجهات",
      contact: "اتصل بنا",
      accommodations: "فيلات خاصة",
      activities: "أنشطة",
      blog: "مجلة الجزيرة"
    },
    cta: {
      planEscape: "خطط لهروبي",
      viewDetails: "عرض التفاصيل",
      explore: "استكشاف",
      learnMore: "أعرف أكثر",
      bookNow: "احجز الآن",
      inquireNow: "استفسر الآن",
      viewAllTours: "عرض جميع الجولات",
      viewAllActivities: "عرض جميع الأنشطة",
      viewAllAccommodations: "عرض جميع أماكن الإقامة",
      visitBlog: "زوروا مدونتنا",
      readMore: "اقرأ المزيد",
      checkAvailability: "التحقق من التوفر",
      reserveSpot: "احجز مكانك"
    },
    sections: {
      about: "حول",
      amenities: "وسائل الراحة",
      rooms: "أماكن الإقامة",
      itinerary: "مسار الرحلة",
      gallery: "معرض الصور",
      requirements: "المتطلبات",
      aboutActivity: "حول هذا النشاط",
      overview: "نظرة عامة"
    },
    activity: {
      pricePerPerson: "السعر للشخص الواحد",
      details: "تفاصيل النشاط"
    },
    footer: {
      newsletter: "النشرة الإخبارية",
      subscribe: "اشتراك",
      placeholder: "بريدك الإلكتروني",
      newsletterText: "اشترك للحصول على أدلة الجزيرة الشهرية وعروض اللحظة الأخيرة الحصرية.",
      copyright: "© {year} {siteName}"
    },
    blog: {
      journal: "مجلة الجزيرة",
      subtitle: "قصص وأدلة وإلهام من الغابة الأفريقية.",
      searchPlaceholder: "بحث عن مقالات...",
      noArticles: "لم يتم العثور على مقالات",
      tryAdjusting: "حاول ضبط الفلاتر أو شروط البحث.",
      clearFilters: "مسح جميع الفلاتر",
      contributor: "مساهم",
      backToJournal: "العودة إلى المجلة",
      author: "المؤلف",
      share: "مشاركة",
      aboutAuthor: "حول المؤلف"
    },
    contact: {
      title: "لنخطط لمغامرة السفاري الخاصة بك",
      subtitle: "خبراء السفاري لدينا هنا لمساعدتك في خلق ذكريات لا تُنسى. من المسارات المخصصة إلى العروض الفورية، نحن على بعد رسالة منك.",
      respondTime: "نرد عادة في غضون ساعتين",
      stats: {
        emergency: "دعم الطوارئ",
        experience: "سنوات الخبرة",
        travelers: "مسافرون سعداء"
      },
      methods: {
        call: "اتصل بنا",
        email: "أرسل بريداً إلكترونياً",
        visit: "زيارة المكتب",
        chat: "الدردشة الحية"
      },
      form: {
        heading: "أرسل لنا رسالة",
        help: "كيف يمكننا مساعدتك؟",
        getQuote: "احصل على عرض سعر",
        customSafari: "سفاري مخصص",
        generalInfo: "معلومات عامة",
        firstName: "الاسم الأول",
        lastName: "اسم العائلة",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        message: "الرسالة",
        placeholder: "أخبرنا عن رحلة سفاري أحلامك...",
        sending: "جاري الإرسال...",
        sendMessage: "إرسال الرسالة",
        success: "تم إرسال الرسالة بنجاح!",
        respondLater: "سنقوم بالرد عليك في غضون 24 ساعة."
      }
    },
    destinations: {
      title: "استكشاف الوجهات",
      subtitle: "رحلة عبر أجمل المناظر الطبيعية في تنزانيا وزنجبار.",
      search: "البحث عن وجهة...",
      tours: "جولات",
      noResults: "لم يتم العثور على وجهات",
      adjustSearch: "حاول ضبط شروط البحث.",
      clearSearch: "مسح البحث",
      overview: "نظرة عامة",
      whyVisit: "لماذا تزور {name}؟",
      bestTime: "أفضل وقت للزيارة",
      keyFacts: "حقائق رئيسية",
      area: "المساحة",
      coordinates: "الإحداثيات",
      planTrip: "خطط لرحلتك"
    },
    accommodations: {
      title: "الأكواخ والمخيمات الفاخرة",
      subtitle: "ملاذات مختارة بعناية حيث تلتقي الفخامة بالبرية.",
      search: "ابحث عن إقامتك...",
      noResults: "لم يتم العثور على أماكن إقامة",
      adjustFilters: "حاول ضبط الفلاتر أو شروط البحث.",
      clearFilters: "مسح جميع الفلاتر",
      priceRange: "نطاق السعر",
      all: "الكل",
      amenities: "وسائل الراحة",
      details: "التفاصيل",
      location: "الموقع",
      rooms: "الغرف المتاحة",
      contactForRates: "اتصل للحصول على الأسعار",
      checkIn: "تسجيل الوصول",
      checkOut: "تسجيل المغادرة",
      diningOptions: "خيارات الطعام",
      noRoomDetails: "معلومات الغرفة المفصلة غير متوفرة. يرجى الاتصال بنا لمعرفة أنواع الغرف المحددة.",
      makeReservation: "قم بالحجز",
      directLine: "الخط المباشر",
      emailReservations: "حجوزات البريد الإلكتروني",
      capacity: "السعة",
      guests: "ضيوف"
    },
    tours: {
      title: "بعثاتنا",
      subtitle: "رحلات منسقة عبر المناظر الطبيعية الجامحة في زنجبار وتنزانيا.",
      search: "البحث عن البعثات...",
      duration: "المدة",
      groupSize: "حجم المجموعة",
      startingFrom: "تبدأ من",
      max: "الحد الأقصى",
      noResults: "لم يتم العثور على بعثات",
      adjustFilters: "حاول ضبط الفلاتر أو شروط البحث.",
      clearFilters: "مسح جميع الفلاتر",
      bestseller: "الأكثر مبيعاً"
    },
    activities: {
      title: "التجارب والأنشطة",
      subtitle: "اكتشف الجواهر الخفية والمغامرات المثيرة التي تنتظرك.",
      search: "بحث عن أنشطة...",
      from: "من",
      noResults: "لم يتم العثور على أنشطة",
      adjustFilters: "حاول ضبط الفلاتر أو شروط البحث.",
      clearFilters: "مسح جميع الفلاتر"
    }
  },
  zh: {
    dashboard: "仪表板",
    createTour: "创建新旅游",
    save: "保存更改",
    cancel: "取消",
    welcome: "欢迎回来",
    stats: {
      revenue: "总收入",
      bookings: "活跃预订"
    },
    loading: "加载中...",
    nav: {
      home: "首页",
      expeditions: "探险",
      destinations: "目的地",
      contact: "联系我们",
      accommodations: "私人别墅",
      activities: "活动",
      blog: "岛屿日志"
    },
    cta: {
      planEscape: "计划我的逃离",
      viewDetails: "查看详情",
      explore: "探索",
      learnMore: "了解更多",
      bookNow: "立即预订",
      inquireNow: "立即咨询",
      viewAllTours: "查看所有行程",
      viewAllActivities: "查看所有活动",
      viewAllAccommodations: "查看所有住宿",
      visitBlog: "访问我们的博客",
      readMore: "阅读更多",
      checkAvailability: "检查空房",
      reserveSpot: "预订位置"
    },
    sections: {
      about: "关于",
      amenities: "设施",
      rooms: "住宿",
      itinerary: "行程",
      gallery: "相册",
      requirements: "要求",
      aboutActivity: "关于此活动",
      overview: "概览"
    },
    activity: {
      pricePerPerson: "人均价格",
      details: "活动详情"
    },
    footer: {
      newsletter: "通讯",
      subscribe: "订阅",
      placeholder: "您的电子邮件",
      newsletterText: "订阅月度岛屿指南和独家最后时刻优惠。",
      copyright: "© {year} {siteName}"
    },
    blog: {
      journal: "岛屿日志",
      subtitle: "来自非洲丛林的故事、指南和灵感。",
      searchPlaceholder: "搜索文章...",
      noArticles: "未找到文章",
      tryAdjusting: "尝试调整您的过滤器或搜索词。",
      clearFilters: "清除所有过滤器",
      contributor: "贡献者",
      backToJournal: "返回日志",
      author: "作者",
      share: "分享",
      aboutAuthor: "关于作者"
    },
    contact: {
      title: "让我们开始您的游猎之旅",
      subtitle: "我们的游猎专家竭诚为您服务，为您创造难忘的回忆。无论是定制行程还是即时报价，只需一条信息，我们就在您身边。",
      respondTime: "通常在 2 小时内回复",
      stats: {
        emergency: "紧急支持",
        experience: "多年经验",
        travelers: "快乐的旅行者"
      },
      methods: {
        call: "给我们打电话",
        email: "给我们发邮件",
        visit: "参观办公室",
        chat: "在线聊天"
      },
      form: {
        heading: "给我们发信息",
        help: "我们能帮您什么？",
        getQuote: "获取报价",
        customSafari: "定制游猎",
        generalInfo: "一般咨询",
        firstName: "名字",
        lastName: "姓氏",
        email: "电子邮件",
        phone: "电话号码",
        message: "信息",
        placeholder: "告诉我们您的梦想之旅...",
        sending: "发送中...",
        sendMessage: "发送信息",
        success: "信息发送成功！",
        respondLater: "我们会在 24 小时内回复您。"
      }
    },
    destinations: {
      title: "探索目的地",
      subtitle: "穿越坦桑尼亚和桑给巴尔最令人叹为观止的景观。",
      search: "寻找目的地...",
      tours: "行程",
      noResults: "未找到目的地",
      adjustSearch: "尝试调整您的搜索词。",
      clearSearch: "清除搜索",
      overview: "概览",
      whyVisit: "为什么访问 {name}？",
      bestTime: "最佳旅游时间",
      keyFacts: "关键信息",
      area: "面积",
      coordinates: "坐标",
      planTrip: "计划您的旅行"
    },
    accommodations: {
      title: "豪华旅馆与营地",
      subtitle: "精心挑选的奢华与荒野交汇的圣地。",
      search: "寻找您的下榻之所...",
      noResults: "未找到住宿",
      adjustFilters: "尝试调整您的过滤器或搜索词。",
      clearFilters: "清除所有过滤器",
      priceRange: "价格范围",
      all: "全部",
      amenities: "设施",
      details: "详情",
      location: "位置",
      rooms: "可用客房",
      contactForRates: "联系获取价格",
      checkIn: "办理入住",
      checkOut: "办理退房",
      diningOptions: "餐饮选项",
      noRoomDetails: "暂无详细客房信息。请联系我们了解具体房型。",
      makeReservation: "立即预订",
      directLine: "直拨电话",
      emailReservations: "邮件预订",
      capacity: "容量",
      guests: "位客人"
    },
    tours: {
      title: "我们的探险",
      subtitle: "穿越桑给巴尔和坦桑尼亚荒野的精心之旅。",
      search: "搜索探险...",
      duration: "时长",
      groupSize: "团队规模",
      startingFrom: "起价",
      max: "最多",
      noResults: "未找到探险行程",
      adjustFilters: "尝试调整您的过滤器或搜索词。",
      clearFilters: "清除所有过滤器",
      bestseller: "畅销"
    },
    activities: {
      title: "体验与活动",
      subtitle: "发现等待您的隐藏秘境和刺激冒险。",
      search: "搜索活动...",
      from: "起",
      noResults: "未找到活动",
      adjustFilters: "尝试调整您的过滤器或搜索词。",
      clearFilters: "清除所有过滤器"
    }
  }
};

